import React, { useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FaSpinner } from 'react-icons/fa';
import Select from 'react-select';
import { guestApi, groupApi } from '../../api/guestList';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../context/AuthContext';

const AddGuestModal = ({ show, onHide, groups, onGuestAdded }) => {
  const { isAuthenticated } = useAuthContext();

  const [submitting, setSubmitting] = useState(false);
  const [newGuest, setNewGuest] = useState({
    jina: '',
    selectedGroups: [],
    namba: '',
    pledge_amount: '',
    paid_amount: '',
    card_type: 'Single',
    email: ''
  });

  const groupOptions = groups?.map(group => ({
    value: group.id,
    label: group.group_name || group.name
  })) || [];

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let phoneStr = String(phone).trim();
    phoneStr = phoneStr.replace(/[^\d+]/g, '');
    if (!phoneStr.startsWith('255') && !phoneStr.startsWith('0')) {
      phoneStr = '0' + phoneStr;
    }
    return phoneStr;
  };

  const formatCurrencyInput = (val) => {
    if (!val && val !== 0) return '';
    const digitsOnly = String(val).replace(/[^\d]/g, '');
    if (!digitsOnly) return '';
    return parseInt(digitsOnly, 10).toLocaleString('en-US');
  };

  const parseCurrencyValue = (val) => {
    if (!val && val !== 0) return 0;
    const digitsOnly = String(val).replace(/[^\d]/g, '');
    return parseFloat(digitsOnly) || 0;
  };

  const handleAddGuest = async () => {
    if (!newGuest.jina || !newGuest.namba) {
      toast.warning('Please fill in name and phone number');
      return;
    }

    setSubmitting(true);
    try {
      const phoneNumber = formatPhoneNumber(newGuest.namba);
      const selectedGroupIds = newGuest.selectedGroups.map(g => g.value);

      const guestData = {
        name: String(newGuest.jina).trim(),
        phone_number: phoneNumber,
        email: newGuest.email || null,
        pledge: parseCurrencyValue(newGuest.pledge_amount),
        fund: parseCurrencyValue(newGuest.paid_amount),
        card_type: newGuest.card_type || 'Single',
        groups: selectedGroupIds
      };

      const response = await guestApi.addGuest(guestData);

      if (response?.success === false || response?.status === 404) {
        toast.error(response.message || 'Failed to add guest');
        setSubmitting(false);
        return;
      }

      const createdGuest = response?.data?.data || response?.data || response;
      const createdGuestId = createdGuest?.id;

      // Attach selected groups via updateGuest (which runs GuestListGroupsMember::firstOrCreate in backend)
      if (createdGuestId && selectedGroupIds.length > 0) {
        try {
          await guestApi.updateGuest(createdGuestId, {
            name: String(newGuest.jina).trim(),
            phone_number: phoneNumber,
            email: newGuest.email || null,
            groups: selectedGroupIds
          });
        } catch (groupErr) {
          console.error('Error attaching guest to groups:', groupErr);
        }
      }

      toast.success('Guest added successfully!');

      setNewGuest({
        jina: '',
        selectedGroups: [],
        namba: '',
        pledge_amount: '',
        paid_amount: '',
        card_type: 'Single',
        email: ''
      });

      onGuestAdded();
      onHide();

    } catch (err) {
      console.error('Error adding guest:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to add guest');
    } finally {
      setSubmitting(false);
    }
  };


  if (!isAuthenticated) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Ongeza Mgeni</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Jina *</Form.Label>
                <Form.Control
                  type="text"
                  value={newGuest.jina}
                  onChange={(e) => setNewGuest({ ...newGuest, jina: e.target.value })}
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Kikundi (Makundi)</Form.Label>
                <Select
                  isMulti
                  options={groupOptions}
                  value={newGuest.selectedGroups}
                  onChange={(selected) => setNewGuest({ ...newGuest, selectedGroups: selected || [] })}
                  placeholder="-- Chagua Kikundi --"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Namba *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 0784859930"
                  value={newGuest.namba}
                  onChange={(e) => setNewGuest({ ...newGuest, namba: e.target.value })}
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={newGuest.email}
                  onChange={(e) => setNewGuest({ ...newGuest, email: e.target.value })}
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Ahadi (TZS)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 100,000"
                  value={newGuest.pledge_amount}
                  onChange={(e) => setNewGuest({ ...newGuest, pledge_amount: formatCurrencyInput(e.target.value) })}
                />
                {newGuest.pledge_amount && (
                  <Form.Text className="text-primary fw-bold d-block mt-1" style={{ fontSize: '0.825rem' }}>
                     TZS {formatCurrencyInput(newGuest.pledge_amount)}
                  </Form.Text>
                )}
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Mchango (TZS)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 50,000"
                  value={newGuest.paid_amount}
                  onChange={(e) => setNewGuest({ ...newGuest, paid_amount: formatCurrencyInput(e.target.value) })}
                />
                {newGuest.paid_amount && (
                  <Form.Text className="text-success fw-bold d-block mt-1" style={{ fontSize: '0.825rem' }}>
                     TZS {formatCurrencyInput(newGuest.paid_amount)}
                  </Form.Text>
                )}
              </Form.Group>
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Funga
        </Button>
        <Button variant="primary" onClick={handleAddGuest} disabled={submitting}>
          {submitting ? <FaSpinner className="fa-spin" /> : 'Hifadhi Mgeni'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddGuestModal;
